/* ==========================================================================
   NOSH "NOTIFY ME" — REAL BACK IN STOCK      [2026-08-21 CX + Ad Readiness]
   ==========================================================================
   This registers a genuine, product-and-variant-specific restock
   subscription with Klaviyo, the back-in-stock backend already installed on
   this store. It is NOT a decorative form: if the real mechanism is not
   present on the page, the CTA never appears at all.

   THE MECHANISM
   -------------
       POST https://a.klaviyo.com/client/back-in-stock-subscriptions/
            ?company_id=<PUBLIC_KEY>
       revision: 2024-10-15
       { data: { type: "back-in-stock-subscription",
                 attributes: { profile: { data: { type:"profile",
                                                  attributes:{ email } } },
                               channels: ["EMAIL"] },
                 relationships: { variant: { data: {
                     type: "catalog-variant",
                     id: "$shopify:::$default:::<VARIANT_ID>" } } } } }

       202 = accepted.  404 variant_not_found = that variant is not in
       Klaviyo's synced catalogue, which we surface as a real error rather
       than pretending it worked.

   Verified end to end from this storefront on 2026-08-21: a bogus variant
   id answers 404 `variant_not_found`, and the real sold-out variant answers
   202. Klaviyo validates the variant against the synced Shopify catalogue
   BEFORE it writes anything.

   NOTE the legacy `/onsite/components/back-in-stock/subscribe` endpoint is
   RETIRED (HTTP 410). Do not go back to it.

   CONSENT: `channels: ["EMAIL"]` on a back-in-stock subscription is a
   restock alert for ONE variant. It is not a newsletter opt-in and it does
   not set marketing consent. We ask for nothing but an email address.
   ========================================================================== */
(function () {
  'use strict';

  var VERSION = 1;
  if (window.NoshNotify && window.NoshNotify.version === VERSION) {
    window.NoshNotify.refresh();
    return;
  }

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2024-10-15';
  var STORE_KEY = 'nosh-notify-done';

  /* ------------------------------------------------------------------ */
  /* Is the real backend actually on this page?                          */
  /* ------------------------------------------------------------------ */

  var companyId = null;

  function detectCompanyId() {
    if (companyId) return companyId;

    /* Primary: read it off Klaviyo's own onsite embed, so the theme keeps
       working untouched if the account key ever changes. */
    var tags = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');
    for (var i = 0; i < tags.length; i++) {
      var m = /[?&]company_id=([A-Za-z0-9]+)/.exec(tags[i].getAttribute('src') || '');
      if (m) {
        companyId = m[1];
        return companyId;
      }
      var seg = /onsite\/js\/([A-Za-z0-9]+)\//.exec(tags[i].getAttribute('src') || '');
      if (seg) {
        companyId = seg[1];
        return companyId;
      }
    }

    /* Secondary: an explicit override on the CTA itself. */
    var host = document.querySelector('[data-nosh-notify][data-nosh-notify-company]');
    if (host) {
      companyId = host.getAttribute('data-nosh-notify-company') || null;
    }
    return companyId;
  }

  function alreadyDone(variantId) {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return false;
      return JSON.parse(raw).indexOf(String(variantId)) !== -1;
    } catch (e) {
      return false;
    }
  }

  function remember(variantId) {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (list.indexOf(String(variantId)) === -1) list.push(String(variantId));
      window.localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(-200)));
    } catch (e) {}
  }

  function subscribe(email, variantId) {
    var cid = detectCompanyId();
    if (!cid) return Promise.reject(new Error('no-klaviyo'));

    return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(cid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
            }
          }
        }
      })
    }).then(function (res) {
      if (res.status === 202 || res.status === 200) return true;
      return res.text().then(function (body) {
        var detail = '';
        try {
          var j = JSON.parse(body);
          if (j.errors && j.errors[0]) detail = j.errors[0].code || j.errors[0].detail || '';
        } catch (e) {}
        var err = new Error(detail || 'http-' + res.status);
        err.status = res.status;
        err.code = detail;
        throw err;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* The shared dialog                                                   */
  /* ------------------------------------------------------------------ */

  var dialog = null;
  var els = {};
  var current = null;
  var lastFocus = null;

  function build() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'nosh-nm';
    dialog.setAttribute('aria-labelledby', 'nosh-nm-title');

    dialog.innerHTML = [
      '<form method="dialog" class="nosh-nm__inner" novalidate>',
      '  <button type="button" class="nosh-nm__close" data-nm-close aria-label="Close">',
      '    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">',
      '      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>',
      '    </svg>',
      '  </button>',
      '  <h2 class="nosh-nm__title" id="nosh-nm-title">Notify me when available</h2>',
      '  <p class="nosh-nm__product" data-nm-product></p>',
      '  <div class="nosh-nm__field" data-nm-variant-wrap hidden>',
      '    <label class="nosh-nm__label" for="nosh-nm-variant">Which one?</label>',
      '    <select class="nosh-nm__select" id="nosh-nm-variant" data-nm-variant></select>',
      '  </div>',
      '  <div class="nosh-nm__field">',
      '    <label class="nosh-nm__label" for="nosh-nm-email">Email address</label>',
      '    <input class="nosh-nm__input" id="nosh-nm-email" data-nm-email type="email"',
      '           inputmode="email" autocomplete="email" placeholder="you@email.com"',
      '           required aria-describedby="nosh-nm-help">',
      '  </div>',
      '  <p class="nosh-nm__help" id="nosh-nm-help">',
      '    We will email you once for this item when it is back. No newsletter, no marketing list.',
      '  </p>',
      '  <p class="nosh-nm__status" data-nm-status role="status" aria-live="polite"></p>',
      '  <button type="submit" class="nosh-nm__submit" data-nm-submit>Notify me</button>',
      '</form>'
    ].join('');

    document.body.appendChild(dialog);

    els.form = dialog.querySelector('form');
    els.product = dialog.querySelector('[data-nm-product]');
    els.email = dialog.querySelector('[data-nm-email]');
    els.status = dialog.querySelector('[data-nm-status]');
    els.submit = dialog.querySelector('[data-nm-submit]');
    els.variantWrap = dialog.querySelector('[data-nm-variant-wrap]');
    els.variant = dialog.querySelector('[data-nm-variant]');

    dialog.querySelector('[data-nm-close]').addEventListener('click', close);
    dialog.addEventListener('close', onClosed);
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close();
    });
    els.form.addEventListener('submit', onSubmit);

    return dialog;
  }

  function setStatus(text, kind) {
    els.status.textContent = text || '';
    els.status.setAttribute('data-nm-state', kind || '');
  }

  function selectedVariantId() {
    if (els.variantWrap.hidden) return current.variantId;
    return els.variant.value || current.variantId;
  }

  function onSubmit(event) {
    event.preventDefault();
    if (!current) return;

    var email = (els.email.value || '').trim();
    if (!email || email.indexOf('@') < 1 || email.lastIndexOf('.') < email.indexOf('@')) {
      setStatus('Please enter a valid email address.', 'error');
      els.email.focus();
      return;
    }

    var variantId = selectedVariantId();
    els.submit.disabled = true;
    els.submit.setAttribute('aria-busy', 'true');
    setStatus('Signing you up…', 'busy');

    subscribe(email, variantId)
      .then(function () {
        remember(variantId);
        setStatus('Done. We will email you as soon as this is back in stock.', 'ok');
        els.submit.textContent = 'You are on the list';
        markSubscribed(variantId);
      })
      .catch(function (err) {
        els.submit.disabled = false;
        els.submit.removeAttribute('aria-busy');
        if (err && err.code === 'variant_not_found') {
          setStatus('We could not set up an alert for this item. Please try again later.', 'error');
        } else if (err && err.message === 'no-klaviyo') {
          setStatus('Alerts are unavailable right now. Please try again later.', 'error');
        } else if (err && err.status === 429) {
          setStatus('Too many requests just now — please try again in a minute.', 'error');
        } else {
          setStatus('Something went wrong. Please check your email address and try again.', 'error');
        }
      });
  }

  /* Reflect success on every CTA on the page for that variant, so a repeat
     click does not look like the first one. */
  function markSubscribed(variantId) {
    var hosts = document.querySelectorAll('[data-nosh-notify]');
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getAttribute('data-nosh-notify-variant') === String(variantId)) {
        var btn = hosts[i].querySelector('[data-nosh-notify-open]');
        if (btn) {
          btn.setAttribute('data-nosh-notify-done', '');
          var lbl = btn.querySelector('[data-nosh-notify-label]');
          if (lbl) lbl.textContent = 'On the list';
        }
      }
    }
  }

  function open(host) {
    build();
    lastFocus = document.activeElement;

    current = {
      variantId: host.getAttribute('data-nosh-notify-variant'),
      title: host.getAttribute('data-nosh-notify-title') || 'this item'
    };

    els.product.textContent = current.title;
    els.email.value = '';
    els.submit.disabled = false;
    els.submit.removeAttribute('aria-busy');
    els.submit.textContent = 'Notify me';
    setStatus('', '');

    /* Variant chooser only when the product genuinely has more than one. */
    var variantsRaw = host.getAttribute('data-nosh-notify-variants');
    var variants = [];
    try {
      variants = variantsRaw ? JSON.parse(variantsRaw) : [];
    } catch (e) {
      variants = [];
    }
    if (variants.length > 1) {
      els.variant.innerHTML = '';
      for (var i = 0; i < variants.length; i++) {
        var o = document.createElement('option');
        o.value = variants[i].id;
        o.textContent = variants[i].title;
        els.variant.appendChild(o);
      }
      els.variant.value = current.variantId;
      els.variantWrap.hidden = false;
    } else {
      els.variantWrap.hidden = true;
    }

    if (alreadyDone(current.variantId)) {
      setStatus('You already asked us to alert you about this one. Sending again is fine.', 'ok');
    }

    document.documentElement.classList.add('nosh-nm-open');
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    window.setTimeout(function () {
      try {
        els.email.focus({ preventScroll: true });
      } catch (e) {
        els.email.focus();
      }
    }, 30);
  }

  function onClosed() {
    document.documentElement.classList.remove('nosh-nm-open');
    current = null;
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try {
        lastFocus.focus({ preventScroll: true });
      } catch (e) {
        lastFocus.focus();
      }
    }
    lastFocus = null;
  }

  function close() {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else {
      dialog.removeAttribute('open');
      onClosed();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Reveal the CTA only where the backend really exists                 */
  /* ------------------------------------------------------------------ */

  function onOpenClick(event) {
    event.preventDefault();
    event.stopPropagation();
    open(event.currentTarget.closest('[data-nosh-notify]'));
  }

  /* ------------------------------------------------------------------ */
  /* Upgrade Dawn's sold-out control on ordinary product cards           */
  /* ------------------------------------------------------------------ */
  /*
     card-product.liquid is a 34KB Dawn file rendered by every collection
     grid, every homepage rail, search and related products. Rather than
     fork it, we upgrade what it already renders: a sold-out card ships a
     .quick-add container holding a DISABLED button plus the variant id in
     input.product-variant-id. That is everything a restock alert needs.

     This means one change covers every card context at once, and the
     no-JavaScript state stays Dawn's own disabled button — a finished
     control, never the blank hole this pass was raised to remove.

     Cards whose variant id cannot be read (multi-variant products, which
     render "Choose options" instead) are deliberately left untouched
     rather than guessed at.
  */
  function cardTitle(card) {
    var el = card.querySelector('.card__heading a') || card.querySelector('.card__heading');
    return el ? (el.textContent || '').trim() : 'this item';
  }

  function enhanceCards() {
    var cards = document.querySelectorAll('.card-wrapper');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.dataset.noshNmCard === '1') continue;
      if (!card.querySelector('.badge--sold-out')) continue;

      var slot = card.querySelector('.quick-add');
      if (!slot || slot.querySelector('[data-nosh-notify]')) continue;

      var input = slot.querySelector('input.product-variant-id');
      var variantId = input && input.value ? input.value.trim() : '';
      if (!variantId) continue;

      card.dataset.noshNmCard = '1';

      /* Hide, do not delete: leaves Dawn's disabled control in the DOM so
         nothing that queries for it breaks, and keeps the card
         unpurchasable by construction. */
      var form = slot.querySelector('product-form');
      if (form) form.hidden = true;

      var host = document.createElement('div');
      host.className = 'nosh-notify';
      host.setAttribute('data-nosh-notify', '');
      host.setAttribute('data-nosh-notify-variant', variantId);
      host.setAttribute('data-nosh-notify-title', cardTitle(card));

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nosh-notify__cta';
      btn.setAttribute('data-nosh-notify-open', '');
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.setAttribute('aria-label', 'Notify me when ' + cardTitle(card) + ' is back in stock');
      btn.innerHTML = '<span data-nosh-notify-label>Notify me</span>';
      btn.hidden = true;

      var fb = document.createElement('span');
      fb.className = 'nosh-notify__fallback';
      fb.setAttribute('data-nosh-notify-fallback', '');
      fb.setAttribute('aria-hidden', 'true');
      fb.textContent = 'Back soon';
      fb.hidden = true;

      host.appendChild(btn);
      host.appendChild(fb);
      slot.appendChild(host);
    }
  }

  function refresh() {
    var live = !!detectCompanyId();
    if (live) enhanceCards();
    var hosts = document.querySelectorAll('[data-nosh-notify]');

    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var btn = host.querySelector('[data-nosh-notify-open]');
      var fallback = host.querySelector('[data-nosh-notify-fallback]');
      if (!btn) continue;

      if (live) {
        btn.hidden = false;
        if (fallback) fallback.hidden = true;
        if (btn.dataset.noshNmBound !== '1') {
          btn.dataset.noshNmBound = '1';
          btn.addEventListener('click', onOpenClick);
        }
        var vid = host.getAttribute('data-nosh-notify-variant');
        if (vid && alreadyDone(vid)) {
          btn.setAttribute('data-nosh-notify-done', '');
          var lbl = btn.querySelector('[data-nosh-notify-label]');
          if (lbl) lbl.textContent = 'On the list';
        }
      } else {
        /* No real backend on this page — never show a button that lies. */
        btn.hidden = true;
        if (fallback) fallback.hidden = false;
      }
    }
  }

  document.addEventListener('shopify:section:load', refresh);
  window.NoshNotify = { version: VERSION, refresh: refresh, open: open, close: close };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
  /* Klaviyo's embed is `async`, so it may land after us. One late re-check. */
  window.addEventListener('load', refresh);
})();
